import React from 'react';
import ReactDOM from 'react-dom';
import { trackingEvent } from '../../../analytics/index';
import { AMPLITUDE_SERVICE } from '../../../analytics/constants';
import { getBasicAmplitudEventProperties } from '../../../analytics/utils';
import { general_layout } from '../../../assets/js/gantt_layout/general-layout';

/**
 * Setup base gantt configuration
 * @param {object} visualizationConfig - Configuration for visualization settings
 * @param {object} gantt - Gantt instance
 * @returns {object} - Configuration object for gantt
 */
export const setupGanttConfig = (visualizationConfig = {}, gantt) => {
  const config = {
    // Base time configuration
    work_time: true,
    duration_unit: 'hour',
    duration_step: 1,
    time_step: 60,
    date_format: '%Y-%m-%d %H:%i',
    hoursPerDay: 8,

    // Undo configuration
    undo_steps: 150,
    undo_actions: {
      update: 'update',
      remove: 'remove',
      add: 'add',
      move: 'move' // move task in grid
    },
    undo_types: {
      link: 'link',
      task: 'task'
    },

    // Visualization settings
    preserve_scroll: false,
    disable_focus_on_create: true,
    link_line_width: 1,
    show_links: visualizationConfig.areLinksVisible ?? false,
    highlight_critical_path: false, // visualizationConfig.isCriticalPathVisible ?? false
    show_slack: visualizationConfig.areSlackVisible ?? false,
    show_baseline: visualizationConfig.areBaselineVisible ?? false,
    show_numtasks: visualizationConfig.areNumtasksVisible ?? false,
    show_todayline: visualizationConfig.isTodaylineVisible ?? false,
    show_submittal_icon: visualizationConfig.areSubmittalsVisible ?? false,

    // Scheduling configuration
    schedule_from_end: true, // Setting this config to true will switch auto scheduling to the as late as possible mode
    auto_scheduling: true,
    auto_scheduling_initial: false,
    round_dnd_dates: false,

    // Multiple selection settings
    multiselect: true,
    multiselect_one_level: false,

    // UI configuration
    task_height: 20,
    scale_height: 28,
    row_height: 30,
    min_grid_column_width: 100,

    // Drag and timeline settings
    drag_timeline: {
      ignore: '.gantt_task_line, .gantt_task_link',
      useKey: 'ctrlKey'
    },

    // Feature settings
    details_on_create: false,
    schedule_from_end: false,
    auto_types: true,

    // Rendering optimizations
    smart_rendering: true,
    static_background: true,
    show_task_cells: true,
    show_progress: true,
    smart_scales: true,
    open_tree_initially: true,
    keyboard_navigation_cells: true,
    order_branch: true,
    order_branch_free: false,
    drag_progress: false,
    fit_tasks: true,

    // Grid and columns
    reorder_grid_columns: true,
    correct_work_time: true,

    // External rendering support
    external_render: {
      // checks the element is a React element
      isElement: (element) => React.isValidElement(element),
      // renders the React element into the DOM
      renderElement: (element, container) => {
        if (gantt && !gantt.completingOnProcess) {
          ReactDOM.render(element, container);
        }
      }
    },

    // Multi task drag selection
    click_drag: {
      callback: null, // Will be set after gantt instance is created
      useKey: 'shiftKey'
    },

    // Layout configuration
    layout: general_layout
  };

  return config;
};

/**
 * Initialize additional gantt properties
 * @param {object} gantt - Gantt instance
 */
export const initializeGanttProperties = (gantt) => {
  // Initialize backup data array
  gantt.backup_data_saving_use = [];

  // Set click_drag callback after gantt instance is available
  gantt.config.click_drag.callback = gantt.onDragCursorMultiTask;
};

/**
 * Setup gantt plugins
 * @returns {object} - Plugins configuration object
 */
export const setupGanttPlugins = () => ({
  auto_scheduling: true,
  critical_path: true,
  click_drag: true,
  multiselect: true,
  drag_timeline: true,
  undo: true,
  keyboard_navigation: true,
  marker: true
});

/**
 * Create tracking event handler for gantt events
 * @returns {Function} - Event handler function
 */
export const createTrackEventHandler = () => (eventName) => {
  trackingEvent(
    eventName,
    { ...getBasicAmplitudEventProperties() },
    AMPLITUDE_SERVICE
  );
};
